import {useState, Fragment} from 'react'
import {useForm} from 'react-hook-form'
import {useMutation} from '@apollo/client'
import {List, Map} from 'immutable'
import PropTypes from 'prop-types'

import AuthenticatorAppIcon from '../../AuthenticatorAppIcon'
import {Button, Cancel, Remove} from '../../../../../dashboard/src/components/blocks/Buttons'
import ButtonGroup from '../../../../../shared_components/core/buttons/ButtonGroup'
import {Box} from '../../../../../dashboard/src/components/blocks/Boxes'
import {ContentBoxGutterMedium, ContentBox} from '../../../../../dashboard/src/components/blocks/ContentBoxes'
import ManageTfaSetupFrame from '../ManageTfaSetupFrame'
import MediumModal from '../../../../../shared_components/mui_base_components/modals/medium_modal/MediumModal'
import SetupAppEnrollment from './SetupAppEnrollment'
import {Text, TextGutterMedium} from '../../../../../dashboard/src/components/blocks/Texts'
import SuccessMessage from './SuccessMessage'

import {TFA_ENROLLMENTS} from '../../../../../graphql/queries/tfa'
import {VERIFY_TFA_ENROLLMENT} from '../../../../../graphql/mutations/tfa'
import {useNotificationContext} from '../../../../../shared_components/notifications/NotificationContext'
import {dateToFromNowDaily} from '../../../../../lib/dateTools'
import {parseProvisioningUri} from '../../../../../lib/tfaTools'
import TfaApi from '../../../web/TfaApi'

const ManageAppEnrollment = props => {
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [status, setStatus] = useState('delete')
  const [error, setError] = useState('')
  const {alwaysNotify} = useNotificationContext()
  const methods = useForm()
  const currentAppEnrollment = props.createdAppEnrollment.isEmpty() ? props.appEnrollment : props.createdAppEnrollment
  const provisioningUri = parseProvisioningUri(currentAppEnrollment.get('provisioning-uri', ''))
  const [verifyTfaEnrollment] = useMutation(VERIFY_TFA_ENROLLMENT, {awaitRefetchQueries: true, refetchQueries: [{query: TFA_ENROLLMENTS}]})

  const copySecret = async event => {
    if (event) event.preventDefault()

    if (!(navigator.clipboard && navigator.clipboard.writeText))
      return

    await navigator.clipboard.writeText(provisioningUri.searchParams.get('secret'))
    alwaysNotify.shortSuccess('Authenticator app secret copied.')
  }

  const toggleModal = () => setShowModal(!showModal)

  const handleSubmit = data => {
    setLoading(true)
    setError('')

    verifyTfaEnrollment({
      variables: {id: currentAppEnrollment.get('id'), input: data},
      onCompleted: () => {
        const tfaConfigId = props.tfaConfig.get('id')
        const payload = {tfaConfigId, enabled: true, enrollmentId: currentAppEnrollment.get('id')}

        props.toggleTfaStatus({
          variables: {input: payload},
          onCompleted: () => {
            if (props.tfaConfig.get('status') !== 'enabled') {
              props.generateBackupCodes(() => {
                TfaApi.fetchTfaConfig(tfaConfigId)
                props.manageTfaSetup('recovery')
                props.resetCreateAppEnrollment()
              })
            } else {
              props.resetCreateAppEnrollment()
              setLoading(false)
              setStatus('success')
              toggleModal()
            }
          }
        })
      },
      onError: () => {
        setError('The code you entered is incorrect. Please try again.')
        setLoading(false)
      }
    })
  }

  const onCancel = () => {
    setDeleting(true)
    props.removeTfaEnrollment({
      id: currentAppEnrollment.get('id'),
      onCompleted: () => {
        props.resetCreateAppEnrollment()
        setDeleting(false)
        props.toggleModal()
      }
    })
  }

  const handleDelete = () => {
    setDeleting(true)
    props.onDelete({id: props.appEnrollment.get('id'),
      onCompleted: () => {
        setDeleting(false)
        props.toggleModal()
        toggleModal()
      }
    })
  }

  const onFinish = () => {
    props.toggleModal()
    setStatus('delete')
  }

  return (
    <Fragment>
      <ManageTfaSetupFrame
        headerIcon={<AuthenticatorAppIcon />}
        headerTitle={`${props.appEnrollment.get('enabled-at') ? 'Manage' : 'Add'} authentication app`}
        headerSubtitle='Configure your authentication app preferences.'
        removeText={(!props.appEnrollment.get('enabled-at') || props.tfaRequired) ? null : 'Remove authentication method'}
        primaryAction={toggleModal}>
        {
          props.createdAppEnrollment.isEmpty() ?
            (
              <ContentBoxGutterMedium className='manage-app-enrollment'>
                <Box className='connected-app body'>
                  <h5 className='app-name'>Enabled</h5>
                  <Text>Last used: {dateToFromNowDaily(props.appEnrollment.get('last-used-at') || props.appEnrollment.get('enabled-at'))}</Text>
                </Box>
              </ContentBoxGutterMedium>
            ) :
            (
              <SetupAppEnrollment
                methods={methods}
                appEnrollment={currentAppEnrollment}
                error={error}
                handleSubmit={handleSubmit}
                copySecret={copySecret}
              />
            )
        }
        {
          !props.createdAppEnrollment.isEmpty() && (
            <ButtonGroup>
              <Cancel onClick={onCancel} processing={deleting} />
              <Button
                disabled={!methods.watch('code')}
                processing={loading}
                onClick={methods.handleSubmit(handleSubmit)}>
                  Save
              </Button>
            </ButtonGroup>
          )
        }
      </ManageTfaSetupFrame>
      <MediumModal className='warning-modal phone-enrollment' open={showModal} onClose={props.toggleModal}>
        <ContentBox className='content flex-container'>
          {
            status === 'delete' ?
              (
                <Fragment>
                  <h2>Remove this authentication method?</h2>
                  <TextGutterMedium>
                    {
                      props.tfaEnrollments.size > 1 ?
                        'This will remove authenticator app verification. Your account is still protected by phone (SMS/Voice) verification.' :
                        'Without two-factor authentication, your password is the only thing protecting your account.'
                    }
                  </TextGutterMedium>
                  <ButtonGroup>
                    <Cancel onClick={toggleModal} />
                    <Remove processing={deleting} onClick={handleDelete}>Continue</Remove>
                  </ButtonGroup>
                </Fragment>
              ) :
              (
                <SuccessMessage mode='app' onFinish={onFinish} />
              )
          }
        </ContentBox>
      </MediumModal>
    </Fragment>
  )
}

ManageAppEnrollment.propTypes = {
  appEnrollment: PropTypes.instanceOf(Map).isRequired,
  appEnrollmentOnly: PropTypes.bool,
  createdAppEnrollment: PropTypes.instanceOf(Map),
  removeTfaEnrollment: PropTypes.func.isRequired,
  generateBackupCodes: PropTypes.func,
  manageTfaSetup: PropTypes.func,
  tfaEnrollments: PropTypes.instanceOf(List).isRequired,
  tfaConfig: PropTypes.instanceOf(Map).isRequired,
  toggleModal: PropTypes.func.isRequired,
  toggleTfaStatus: PropTypes.func.isRequired,
  resetCreateAppEnrollment: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  tfaRequired: PropTypes.bool
}

export default ManageAppEnrollment
